import { DatePipe, DecimalPipe, Location } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, Subscription, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";

import { EventEmitter } from "@angular/core";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  DialogSize,
  OperationType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { NamedType } from "../../model/domain/Tipo";
import { Transfer } from "../../model/domain/Transfer";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-transfer",
  templateUrl: "./transfer.component.html",
  styleUrls: ["./transfer.component.css"],
})
export class TransferComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private snackBarService: SnackbarService,
    private cutoffService: CutoffService,
    private authService: AuthService,
    private dialogService: DialogService,
    private location: Location,
    private loaderService: LoaderService,
    private domainService: DomainService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isSubmit) {
          this.resetForm();
        }
      }
    });
  }

  navigationSubscription: Subscription;

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundComponent: DadosFundoComponent;
  @ViewChild("receiver", { static: true })
  receiverComponent: DadosFundoComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  ListaTipoOperacao: NamedType<number>[];
  dataChanged: EventEmitter<string>;

  decimalPipe = new DecimalPipe("pt");

  TicketTypeEnum = TicketType;

  isEditing = false;
  canValidate = false;
  isSubmit = false;

  model: Transfer = null;
  isNotProduction = environment.name != "production";

  validators = {
    operationTypeId: (value) =>
      [OperationType.TED, OperationType.DOC].includes(value),
  };

  ngOnInit() {
    this.isSubmit = false;
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.model = new Transfer();
    this.fundComponent.portfolio = new Portfolio();
    this.receiverComponent.portfolio = new Portfolio();

    this.frm.reset();
    this.frm.resetForm();

    this.fundComponent.resetForm();
    this.receiverComponent.resetForm();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;

    this.isSubmit = false;
    this.model.id = 0;
    this.model.operationDate = new Date();

    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null): void {
    this.model = new Transfer();
    this.ListaTipoOperacao = this.service.ListTransferOperationTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;

    if (id) {
      this.service.Transfer.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.fundComponent.fillComponentFieldsLoad(this.model);
          this.receiverComponent.fillReceiverComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Transfer])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit(): void {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.isSubmit = false;
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundComponent.validatePortfolioFields();
    const fundErrors = this.fundComponent.getErrors();

    this.receiverComponent.validatePortfolioFields();
    const receiverErrors = this.receiverComponent.getErrors();

    if (
      f.invalid ||
      errors.length > 0 ||
      fundErrors.length > 0 ||
      receiverErrors.length > 0
    ) {
      this.isSubmit = true;
      return throwError("Verifique os campos destacados.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.operationValue = this.validator.getCurrency(data.operationValue);

    if (data.operationDate) {
      data.operationDate = new Date(
        data.operationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    data = this.fundComponent.fillPortfolioData(
      data,
      this.fundComponent.portfolio
    );
    data = this.receiverComponent.fillReceiverData(
      data,
      this.receiverComponent.portfolio
    );
    data.annotations = this.annotationComponent.annotation;

    if (f.invalid) {
      this.isSubmit = true;
      return throwError("Verifique os campos destacados.");
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.Transfer.updateTicket(this.model.id, data)
        : this.service.Transfer.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          this.isSubmit = true;
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((transfer: Transfer) => {
          if (!editing) {
            this.model.id = transfer.id;
            this.model.operationDate = transfer.operationDate;
            this.model.statusId = transfer.statusId;
            this.model.accountManagerName = transfer.accountManagerName;
            this.model.accountManagerDocument = transfer.accountManagerDocument;
            this.model.ticketCutoffTypeId = transfer.ticketCutoffTypeId;
            this.model.portfolioFundId = transfer.portfolioFundId;

            this.isEditing = true;
            this.isSubmit = false;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = "A boleta não poderá mais ser editada, deseja continuar?";

    if (this.isNotProduction) {
      textConfirm +=
        "<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.";
    }

    return this.dialogService
      .confirm("Encaminhar Boleta", textConfirm)
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (fixedIncome: Transfer) => {
            this.service.Transfer.forwardTicket(fixedIncome.id).subscribe(
              (res) => {
                this.model.statusDescription = res.statusDescription;

                this.setEnable(false);

                this.snackBarService.open("Boleta encaminhada.", "OK", {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                });

                this.getTicket(fixedIncome.id);
              },
              (result) => {
                if (
                  result.status === 401 &&
                  result.error &&
                  result.error.message === "invalid-banks"
                ) {
                  this.snackBarService.open(
                    "Os bancos selecionados são inválidos",
                    "OK"
                  );
                }
              }
            );
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Transfer.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    if (this.hasErrorElectronicTransfer()) {
      this.dialogService
        .confirm(
          "Aprovar Boleta",
          "ATENÇÃO: O status da Transferência TED/TEF nesta boleta está com erro. Isto significa que a transferência não aconteceu e a liquidação deve ser feita de forma manual. Deseja aprovar a boleta mesmo assim?",
          "Aprovar mesmo assim",
          "Cancelar",
          DialogSize.Medium
        )
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    } else {
      this.dialogService
        .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    }
  }

  private ticketApprove() {
    this.service.approve(this.model.id, "").subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");

      this.getTicket(this.model.id);
    });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );

              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate(): void {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 60000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.Transfer },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canEdit(): boolean {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketTransfer)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Transfer;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketTransfer) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketTransfer)
    );
  }

  fillReceiverData(data) {
    this.receiverComponent.selectedFundAccount = null;

    this.receiverComponent.selectedFund = data.selectedFund;
    this.receiverComponent.fundAccountList = data.fundAccountList;
    this.receiverComponent.fillComponentWhenFundSelected();
  }

  fillFundAccountList(data) {
    this.receiverComponent.selectedFundAccount = null;
    // this.receiverComponent.fundAccountList = null;

    let newList = this.fundComponent.fundAccountList;

    newList = newList.filter((x) => x.id !== data.id);

    this.receiverComponent.fundAccountList = newList;

    if (this.receiverComponent.fundAccountList.length === 1) {
      this.receiverComponent.selectedFundAccount =
        this.receiverComponent.fundAccountList[0];
      this.receiverComponent.fillComponentWhenFundAccountSelected();
    }
  }

  disableActionButtons() {
    let disabled =
      this.model.transferStatusDescription == "Transferência em Trânsito" ||
      this.model.transferStatusDescription == "Transferência Pendente" ||
      this.model.transferStatusDescription == "Transferência Agendada";

    return disabled;
  }

  hasErrorElectronicTransfer() {
    return this.model.transferStatusDescription == "Transferência com Erro";
  }

  showTransferStatus() {
    return (
      this.model.id != null &&
      this.model.id != 0 &&
      this.model.transferStatusDescription != null &&
      this.model.transferStatusDescription != ""
    );
  }
}
